/**
 * Universal API Service for Admin Panel
 * Handles authentication, school context, and error handling consistently
 */
import { API_BASE_URL } from "../config";

class APIService {
    constructor(baseURL = API_BASE_URL) {
        this.baseURL = baseURL;
    }

    // Get authentication headers with JWT token
    getAuthHeaders() {
        // Support both old and new token keys for compatibility
        const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
        if (!token) {
            throw new Error('No authentication token found');
        }
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    // Handle API responses consistently
    async handleResponse(response) {
        if (response.status === 401) {
            // Token expired or invalid - redirect to login
            this.logout();
            throw new Error('Authentication required');
        }

        if (response.status === 403) {
            throw new Error('You do not have permission to perform this action');
        }

        if (response.status === 404) {
            throw new Error('The requested resource was not found');
        }

        if (response.status >= 500) {
            throw new Error('Server error. Please try again later');
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            return response.json();
        }
        return response.text();
    }

    // Generic GET request
    async get(endpoint) {
        try {
            const response = await fetch(`${this.baseURL}${endpoint}`, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        } catch (error) {
            console.error(`GET ${endpoint} failed:`, error);
            throw error;
        }
    }

    // Generic POST request
    async post(endpoint, data) {
        try {
            const response = await fetch(`${this.baseURL}${endpoint}`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(data)
            });
            return this.handleResponse(response);
        } catch (error) {
            console.error(`POST ${endpoint} failed:`, error);
            throw error;
        }
    }

    // Generic PUT request
    async put(endpoint, data) {
        try {
            const response = await fetch(`${this.baseURL}${endpoint}`, {
                method: 'PUT',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(data)
            });
            return this.handleResponse(response);
        } catch (error) {
            console.error(`PUT ${endpoint} failed:`, error);
            throw error;
        }
    }

    // Generic PATCH request
    async patch(endpoint, data) {
        try {
            const response = await fetch(`${this.baseURL}${endpoint}`, {
                method: 'PATCH',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(data)
            });
            return this.handleResponse(response);
        } catch (error) {
            console.error(`PATCH ${endpoint} failed:`, error);
            throw error;
        }
    }

    // Generic DELETE request
    async delete(endpoint) {
        try {
            const response = await fetch(`${this.baseURL}${endpoint}`, {
                method: 'DELETE',
                headers: this.getAuthHeaders()
            });
            return this.handleResponse(response);
        } catch (error) {
            console.error(`DELETE ${endpoint} failed:`, error);
            throw error;
        }
    }

    // File upload with proper headers
    async uploadFile(endpoint, formData) {
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${this.baseURL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Don't set Content-Type for FormData - browser will set it with boundary
                },
                body: formData
            });
            return this.handleResponse(response);
        } catch (error) {
            console.error(`File upload to ${endpoint} failed:`, error);
            throw error;
        }
    }

    // Download file with proper authentication
    async downloadFile(endpoint, filename = null) {
        try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
            const response = await fetch(`${this.baseURL}${endpoint}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }

            const blob = await response.blob();

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'download';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return blob;
        } catch (error) {
            console.error(`File download from ${endpoint} failed:`, error);
            throw error;
        }
    }

    // Check if user is authenticated
    isAuthenticated() {
        const token = localStorage.getItem('access_token') || localStorage.getItem('accessToken');
        const schoolId = localStorage.getItem('school_id');
        return !!(token && schoolId);
    }

    // Get current user's school ID
    getSchoolId() {
        return localStorage.getItem('school_id');
    }

    // Get current user's role
    getUserRole() {
        return localStorage.getItem('user_role');
    }

    // Get current user's email
    getUserEmail() {
        return localStorage.getItem('user_email');
    }

    // Get authentication token
    getToken() {
        return localStorage.getItem('access_token') || localStorage.getItem('accessToken');
    }

    // Logout user
    logout() {
        // Clear both old and new token keys
        localStorage.removeItem('accessToken');
        localStorage.removeItem('access_token');
        localStorage.removeItem('school_id');
        localStorage.removeItem('user_role');
        localStorage.removeItem('user_email');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('school_name');

        // Redirect to login page
        if (typeof window !== 'undefined') {
            window.location.href = '/';
        }
    }

    // Build query string from parameters
    buildQueryString(params) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                queryParams.append(key, value);
            }
        });
        return queryParams.toString();
    }

    // GET request with query parameters
    async getWithParams(endpoint, params = {}) {
        const queryString = this.buildQueryString(params);
        const url = queryString ? `${endpoint}?${queryString}` : endpoint;
        return this.get(url);
    }

    // Health check endpoint
    async healthCheck() {
        try {
            const response = await fetch(`${this.baseURL}/health`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            return response.ok;
        } catch (error) {
            console.error('Health check failed:', error);
            return false;
        }
    }

    // Enhanced error handling for admin panel
    handleError(error) {
        console.error('Admin API Error:', error);

        if (error.message.includes('401') || error.message.includes('Authentication required')) {
            this.logout();
            return 'Session expired. Please login again.';
        }

        if (error.message.includes('403') || error.message.includes('permission')) {
            return 'Access denied. Insufficient permissions.';
        }

        if (error.message.includes('404') || error.message.includes('not found')) {
            return 'Resource not found.';
        }

        if (error.message.includes('500') || error.message.includes('Server error')) {
            return 'Server error. Please try again later.';
        }

        if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
            return 'Network error. Please check your connection.';
        }

        return error.message || 'An unexpected error occurred';
    }
}

export default APIService;